import React, { useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import type { IFilter } from "../../components/common/CommonFilter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  CheckCircle2,
  Clock,
  Eye,
  IndianRupee,
  MoreHorizontal,
  Users,
  Wallet,
} from "lucide-react";
import { showToastNotification } from "../../utility/helper";
import { Button } from "../../components/ui/button";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import CommonFilter from "../../components/common/CommonFilter";
import CommonTable, {
  type TableColumn,
} from "../../components/common/CommonTable";
import TablePagination from "../../components/common/TablePagination";
import { RoutesLink } from "../../utility/RouteLinks";

// TODO: move to shared interface file (e.g. src/interface/wage.interface.ts)
interface IWageRecord {
  id: string;
  labor: { id: string; name: string };
  site: { id: string; name: string };
  designation?: string;
  daysWorked: number;
  dailyRate: number;
  advance: number;
  status: "paid" | "pending";
}

export interface IPageInfo {
  currentPage: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

type WageTab = "all" | "paid" | "pending";

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Paid", value: "paid" },
  { label: "Pending", value: "pending" },
];

const inr = (value: number) =>
  `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// ── Small stat card, local to this page — promote to a shared component if reused elsewhere ──
const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
}> = ({ label, value, icon }) => (
  <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-4 flex items-center gap-3">
    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
        {label}
      </p>
      <p className="text-lg font-semibold text-gray-800 truncate">{value}</p>
    </div>
  </div>
);

const Wages: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading] = useState(false);
  const [wageList, setWageList] = useState<IWageRecord[]>([]);
  const [activeTab, setActiveTab] = useState<WageTab>("all");
  const [period, setPeriod] = useState<string>(moment().format("YYYY-MM"));

  const [filter, setFilter] = useState<IFilter & { siteId?: string }>({
    search: "",
    status: "",
    siteId: "",
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // ── Fetch wage data ──────────────────────────────────────────────────────
  // page = paginationData.currentPage, limit = paginationData.pageSize
  // const fetchWages = async () => {
  //   setIsLoading(true);
  //   try {
  //     // TODO: Replace with actual API call
  //     // const params = GetFilterUrl({ ...filter, period });
  //     // const PATH = `pageNumber=${page}&pageSize=${limit}${params ? `&${params}` : ""}`;
  //     // const response = await WageService.getAllWages(PATH);
  //     // if (response?.status) {
  //     //   setWageList(response.data.wages || []);
  //     //   setPaginationData(prev => ({
  //     //     ...prev,
  //     //     total: response.data.pageInfo?.total || 0,
  //     //     totalPages: response.data.pageInfo?.totalPages || 0,
  //     //   }));
  //     // }

  //     // --- Mock data for demo ---
  //     const mockData: IWageRecord[] = [
  //       {
  //         id: "w1",
  //         labor: { id: "1", name: "Ramesh Kumar" },
  //         site: { id: "s1", name: "Site 4 — Roofing" },
  //         designation: "Mason",
  //         daysWorked: 26,
  //         dailyRate: 650,
  //         advance: 2000,
  //         status: "paid",
  //       },
  //       {
  //         id: "w2",
  //         labor: { id: "2", name: "Sunita Devi" },
  //         site: { id: "s2", name: "Site 2 — Block A" },
  //         designation: "Helper",
  //         daysWorked: 24,
  //         dailyRate: 600,
  //         advance: 0,
  //         status: "pending",
  //       },
  //       {
  //         id: "w3",
  //         labor: { id: "3", name: "Arvind Yadav" },
  //         site: { id: "s1", name: "Site 4 — Roofing" },
  //         designation: "Mason",
  //         daysWorked: 18,
  //         dailyRate: 650,
  //         advance: 1500,
  //         status: "pending",
  //       },
  //       {
  //         id: "w4",
  //         labor: { id: "4", name: "Manoj Prajapati" },
  //         site: { id: "s4", name: "Site 3 — Warehouse Extension" },
  //         designation: "Electrician",
  //         daysWorked: 22,
  //         dailyRate: 750,
  //         advance: 0,
  //         status: "paid",
  //       },
  //     ];
  //     setWageList(mockData);
  //     setPaginationData((prev) => ({ ...prev, total: mockData.length, totalPages: 1 }));
  //   } catch (error) {
  //     ThrowError(error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   fetchWages(paginationData.currentPage, paginationData.pageSize);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [paginationData.currentPage, paginationData.pageSize, filter, period]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleTabChange = (val: string) => {
    const tab = val as WageTab;
    setActiveTab(tab);
    setFilter((prev) => ({ ...prev, status: tab === "all" ? "" : tab }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleFilterChange = (value: string, name: string) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const handleMarkPaid = (id: string) => {
    // TODO: Replace with actual API call — WageService.markAsPaid(id)
    setWageList((prev) =>
      prev.map((w) => (w.id === id ? { ...w, status: "paid" } : w)),
    );
    showToastNotification("success", "Marked as paid");
  };

  // ── Derived amounts ──────────────────────────────────────────────────────
  const withNet = wageList.map((w) => ({
    ...w,
    grossWage: w.daysWorked * w.dailyRate,
    netPayable: w.daysWorked * w.dailyRate - w.advance,
  }));

  const totalWorkers = withNet.length;
  const totalGross = withNet.reduce((sum, w) => sum + w.grossWage, 0);
  const totalPaid = withNet
    .filter((w) => w.status === "paid")
    .reduce((sum, w) => sum + w.netPayable, 0);
  const totalPending = withNet
    .filter((w) => w.status === "pending")
    .reduce((sum, w) => sum + w.netPayable, 0);

  // ── Additional filters (site) ────────────────────────────────────────────
  const additionalFilters = (
    <div className="text-left">
      {/* TODO: Replace with actual SearchSelect fetched from Site API */}
      <select
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-primary focus:border-primary"
        value={filter.siteId || ""}
        onChange={(e) => handleFilterChange(e.target.value, "siteId")}
      >
        <option value="">All Sites</option>
        <option value="s1">Site 4 — Roofing</option>
        <option value="s2">Site 2 — Block A</option>
        <option value="s4">Site 3 — Warehouse Extension</option>
      </select>
    </div>
  );

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns: TableColumn<(typeof withNet)[number]>[] = [
    {
      key: "sNo",
      header: "S.No",
      render: (_item, index) => (
        <span className="text-muted-foreground font-medium text-sm">
          {(paginationData.currentPage - 1) * paginationData.pageSize +
            index +
            1}
        </span>
      ),
    },
    {
      key: "labor",
      header: "Worker",
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm shrink-0">
            {item.labor.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-700 truncate">
              {item.labor.name}
            </p>
            <p className="text-xs text-gray-400">{item.designation || "—"}</p>
          </div>
        </div>
      ),
    },
    {
      key: "site",
      header: "Site",
      render: (item) => <span>{item.site.name}</span>,
    },
    {
      key: "daysWorked",
      header: "Days",
      render: (item) => <span className="font-medium">{item.daysWorked}</span>,
    },
    {
      key: "dailyRate",
      header: "Daily Rate",
      render: (item) => <span>{inr(item.dailyRate)}</span>,
    },
    {
      key: "grossWage",
      header: "Gross Wage",
      render: (item) => (
        <span className="font-medium">{inr(item.grossWage)}</span>
      ),
    },
    {
      key: "advance",
      header: "Advance",
      render: (item) => (
        <span className="text-gray-500">
          {item.advance ? `- ${inr(item.advance)}` : "—"}
        </span>
      ),
    },
    {
      key: "netPayable",
      header: "Net Payable",
      render: (item) => (
        <span className="font-semibold text-gray-800">
          {inr(item.netPayable)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${
            item.status === "paid"
              ? "text-green-600 border-green-600 bg-green-50"
              : "text-amber-600 border-amber-600 bg-amber-50"
          }`}
        >
          {item.status === "paid" ? "Paid" : "Pending"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
              <MoreHorizontal className="h-4 w-4 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() =>
                navigate(`${RoutesLink.laborDetail}/${item.labor.id}`)
              }
              className="cursor-pointer"
            >
              <Eye className="h-4 w-4 mr-2" /> View Worker
            </DropdownMenuItem>
            {item.status === "pending" && (
              <DropdownMenuItem
                onClick={() => handleMarkPaid(item.id)}
                className="cursor-pointer text-green-600"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" /> Mark as Paid
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Heading & Period selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <MainHeading title="Wages" />
        <div className="flex items-center gap-2.5">
          <label className="text-sm font-medium text-gray-500 hidden sm:block">
            Period
          </label>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
          />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Workers"
          value={totalWorkers}
          icon={<Users size={18} className="text-primary" />}
        />
        <StatCard
          label="Total Gross Wages"
          value={inr(totalGross)}
          icon={<IndianRupee size={18} className="text-primary" />}
        />
        <StatCard
          label="Paid"
          value={inr(totalPaid)}
          icon={<Wallet size={18} className="text-primary" />}
        />
        <StatCard
          label="Pending"
          value={inr(totalPending)}
          icon={<Clock size={18} className="text-primary" />}
        />
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="bg-slate-100/80 flex py-5 flex-nowrap overflow-x-auto h-fit gap-2 rounded-lg border border-slate-200/60 shadow-inner mb-6 scroll-bar-hidden items-center">
          <TabsTrigger
            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white"
            value="all"
          >
            All
          </TabsTrigger>
          <TabsTrigger
            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white"
            value="paid"
          >
            Paid
          </TabsTrigger>
          <TabsTrigger
            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white"
            value="pending"
          >
            Pending
          </TabsTrigger>
        </TabsList>

        {(["all", "paid", "pending"] as WageTab[]).map((tab) => (
          <TabsContent key={tab} value={tab} className="space-y-4">
            <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
              <CommonFilter
                searchValue={filter.search || ""}
                onSearchChange={handleSearchChange}
                value={filter}
                filterOptions={STATUS_OPTIONS}
                filterName="status"
                filterPlaceholder="Select Status"
                onChange={handleFilterChange}
                additionalFilters={additionalFilters}
              />
              <CommonTable
                data={withNet}
                columns={columns}
                loading={isLoading}
                stickyColumns={2}
              />
              <TablePagination
                paginationData={paginationData}
                setPaginationData={setPaginationData}
                handleChangePageSize={handleChangePageSize}
                isLoading={isLoading}
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default Wages;
